// ============================================================================
// AvecSyncContext — Distribui dados da Avec + orquestra sync automático (60s)
// ============================================================================
// Consumo em qualquer componente:
//   const { data, loading, lastSync, refresh, selectedMonth } = useAvecSync();
//
// data.units['moema'] → KPIs da Moema
// data.network → KPIs agregados da rede
// ============================================================================

import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import {
  syncAllUnitsMonth, syncAllUnitsRange, getCache, setCache, consolidateClosedMonths, getSnapshots,
  syncDailyFaturamentoMonth, getDailyCache, getDailyFaturamentoForMonth,
  isSnapshotEmpty, syncProExtrasRange, syncProBloqueiosRange,
  retentarFalhasRange, transferirBrutos, recalcularRede,
} from '../services/avecSync.js';
import { criarRangeStore } from '../utils/rangeStore.js';
import { isAvecConfigured } from '../config/avecConfig.js';
import { useAuth } from './AuthContext.jsx';
import { useData } from './DataContext.jsx';
import { cloudSave } from '../utils/supabase.js';
import { mergeMaxWins, prepararMesParaGravar } from '../utils/avecMesCache.js';
import { mesTemFalhas } from '../utils/avecFalhas.js';
import { CHAVE_RESSYNC, lerMarcasRessync, decidirRessync, registrarTentativa, limparMarcas } from '../utils/avecRessync.js';
import { PRIORIDADE } from '../utils/avecRetentativa.js';
import {
  AVEC_COLETA_ESTADO, DASH_PEDIDO_ENXUTO, RANGE_CACHE_PROTEGIDO, AVEC_SYNC_SO_COM_SESSAO, AVEC_ULTIMO_SYNC_JSON, AVEC_LIMITADOR,
  CRM_SEM_RECARGA,
  AVEC_SYNC_PERIODICO,
} from '../config/featureFlags.js';
import { pollingGlobalPausado, ouvirRotaCrm } from '../utils/crmRotaAtiva.js';

// 27/10/2026 (CRM_SEM_RECARGA): com o CRM aberto o polling de 60 s pula a rodada (volta ao sair)
const pausadoNoCrm = () => pollingGlobalPausado({ ligado: CRM_SEM_RECARGA });
import { CHAVE_SYNC_APARELHO, lerUltimoSyncMs, valorUltimoSyncParaGravar, syncAtrasado } from '../utils/avecUltimoSync.js';

const DEFAULT_INTERVAL_MS = 60 * 1000; // 60s conforme confirmado pelo usuário
const MIN_INTERVAL_MS = 30 * 1000;     // nunca menos que 30s (proteção)

const AvecSyncContext = createContext(null);

// Merge MAX-WINS (mesmo código de sempre) mora em src/utils/avecMesCache.js
// desde 23/10/2026, junto do passo que protege o que falhou (testável no node).
// Compat alias (o doSync e os 12 meses passam por prepararMesParaGravar)
export const mergePreservingNonEmpty = mergeMaxWins;

export function AvecSyncProvider({ children, intervalMs = DEFAULT_INTERVAL_MS }) {
  // Sync em tempo real: TODOS os dispositivos sincronizam com a API Avec a
  // cada 60s. O TTL distribuído de 5min foi removido em 29/04/2026 — havia
  // sido instalado quando achávamos que estávamos sobrecarregando a API,
  // mas a causa real era desconfiguração no lado da Avec (Franquia errada).
  // Agora que a API voltou ao normal, queremos atualização instantânea
  // pra todos os profissionais e admins.
  //
  // Proteções que permanecem ativas:
  //   - inflightRef: evita chamadas concorrentes no MESMO device
  //   - mergeMaxWins: rejeita quedas de >30% (protege contra resposta parcial)
  //   - autoSync flag: usuário pode pausar manualmente se quiser
  const { user } = useAuth();
  const { cloudLoaded, cloudReloadTick } = useData();
  // A8a (23/10/2026): aparelho sem sessão válida (tela de login, sessão legada)
  // não consulta o Avec no fundo. Ao logar começa, ao sair para.
  const temSessao = !AVEC_SYNC_SO_COM_SESSAO || !!user?.sessionToken;
  const temSessaoRef = useRef(temSessao);
  temSessaoRef.current = temSessao;
  const SYNC_META_KEY = 'fk_avec_last_sync_at'; // Mantido só para telemetria
  // Marca timestamp do último sync — usado pra exibir "última atualização" e
  // permitir que dispositivos saibam quando outro device atualizou o cache.
  const markSyncDone = () => {
    if (AVEC_ULTIMO_SYNC_JSON) {
      // A8b: chave só do aparelho (o cofre não sobrescreve) + o formato JSON que
      // o cofre usa; nada sobe para a nuvem (o upload falhava sempre antes).
      const agoraMs = Date.now();
      try {
        localStorage.setItem(CHAVE_SYNC_APARELHO, valorUltimoSyncParaGravar(agoraMs));
        localStorage.setItem(SYNC_META_KEY, JSON.stringify(new Date(agoraMs).toISOString()));
      } catch { /* armazenamento cheio/bloqueado: só telemetria */ }
      return;
    }
    const now = new Date().toISOString();
    localStorage.setItem(SYNC_META_KEY, now);
    cloudSave(SYNC_META_KEY, now).catch(() => {});
  };

  // Mês selecionado (default = mês corrente)
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);

  // Cache por mês: { 'YYYY-MM': {units, network} }
  const [cache, setCacheState] = useState(() => getCache());
  const [snapshots, setSnapshotsState] = useState(() => getSnapshots());
  const [dailyCache, setDailyCacheState] = useState(() => getDailyCache());

  // Sempre que o DataContext termina um loadFromCloud (boot inicial OU
  // auto-refresh de 60s OU botão Atualizar manual), re-lê os caches Avec
  // do localStorage. Sem isso, o estado do AvecSync fica preso ao que viu
  // no 1º mount — bug que aparecia pra profissionais cujo cache inicial
  // estava parcial: mesmo com cloud trazendo dados frescos via auto-refresh,
  // o AvecSync continuava mostrando vazio até a próxima chamada à API.
  useEffect(() => {
    if (!cloudLoaded) return;
    setCacheState(getCache());
    setSnapshotsState(getSnapshots());
    setDailyCacheState(getDailyCache());
  }, [cloudLoaded, cloudReloadTick]);

  // Status de sync
  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null); // Date
  const [error, setError] = useState(null);
  const [autoSync, setAutoSync] = useState(true);

  const intervalRef = useRef(null);
  const inflightRef = useRef(false); // previne chamadas concorrentes
  const inflightStartRef = useRef(0); // timestamp do início da chamada em curso
  // Ref pra última versão do doSync — permite que o setInterval sempre chame
  // a função atual, mesmo após `cache` mudar e o useCallback reconstruir doSync.
  // Sem isso, o interval fica preso na referência inicial (cache vazio).
  const doSyncRef = useRef(null);
  // Rastreia meses passados (recém-fechados) que já tiveram refetch FORÇADO
  // de daily nesta sessão — garante que os valores do último dia do mês
  // anterior sejam finalizados (capta comandas tardias que entraram depois
  // do último sync antes da virada do dia).
  const dailyFinalizeDoneRef = useRef(new Set());

  const monthKey = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;

  // Verifica se o mês selecionado é consolidado (não sincronizar)
  const isConsolidated = Boolean(snapshots[monthKey]);
  // Verifica se o mês é passado (já fechou)
  const isPastMonth = (() => {
    const currKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return monthKey < currKey;
  })();

  // Retorna true se pelo menos uma unidade tem dados reais (não-zero)
  const hasAnyRealData = (data) =>
    data?.units && Object.values(data.units).some(
      u => !u._error && ((u.faturamento || 0) > 0 || (u.visitas || 0) > 0)
    );

  // ─── Função de sync ─────────────────────────────────────────────────────
  // opts.force = true → bypassa o mergeMaxWins. Aceita o valor da API direto.
  // Usado pelo botão "Atualizar" quando o usuário quer forçar recálculo.
  const doSync = useCallback(async (forceYear, forceMonth, opts = {}) => {
    if (!isAvecConfigured()) {
      setError('Avec API não configurada');
      return;
    }
    if (!temSessaoRef.current) return; // A8a: sem sessão não chama o Avec
    // Anti-trava: se a chamada anterior está em curso há mais de 90s,
    // assume que travou e libera. (timeout API = 30s × 2 retries = 90s teto)
    // 23/10/2026: com a fila (AVEC_LIMITADOR) uma rodada pode passar disso; o
    // intervalo de 60 s não muda, o tick só é pulado com a rodada ainda em curso.
    if (inflightRef.current) {
      const stuck = Date.now() - inflightStartRef.current > (AVEC_LIMITADOR ? 240_000 : 90_000);
      if (!stuck) return;
      console.warn('[AvecSync] inflight travado >90s — liberando');
      inflightRef.current = false;
    }
    inflightRef.current = true;
    inflightStartRef.current = Date.now();
    setLoading(true);
    setError(null);
    try {
      const y = forceYear ?? selectedYear;
      const m = forceMonth ?? selectedMonth;
      // prioridade de FUNDO: 1 tentativa (a próxima rodada de 60 s é a nova tentativa)
      const data = await syncAllUnitsMonth(y, m, { prioridade: PRIORIDADE.FUNDO });
      const key = `${y}-${String(m).padStart(2, '0')}`;

      // SEMPRE marca timestamp do último sync, mesmo se vazio — assim sabemos
      // se o polling está vivo. Antes, quando a API retornava vazio (token
      // ruim, rate limit, etc), o markSyncDone NUNCA era chamado e o
      // last_sync_at no Supabase ficava congelado por dias, mascarando que
      // o polling estava na verdade rodando.
      markSyncDone();
      setLastSync(new Date());

      if (!hasAnyRealData(data)) {
        console.warn('[AvecSync] API retornou sem dados reais — cache não atualizado');
        return;
      }
      // Lê o cache mais recente DIRETO do localStorage (sincronamente)
      // pra evitar race condition: setCacheState com callback é assíncrono,
      // newCache ficava undefined nas linhas seguintes → consolidateClosedMonths
      // recebia undefined → Object.entries(undefined) → THROW "erro na sync".
      const currentCache = getCache();
      const prev = currentCache[key];
      // A1.5: com o estado da coleta, relatório que falhou herda o valor da coleta
      // anterior do mesmo mês (inclusive no force do botão Atualizar) e o que
      // continua faltando fica null; sem falha, o objeto é o mesmo de antes.
      const mergedData = prepararMesParaGravar(prev, data, { force: !!opts.force, coletaEstado: AVEC_COLETA_ESTADO });
      const newCache = { ...currentCache, [key]: mergedData };
      setCacheState(newCache);
      setCache(newCache);
      cloudSave('fk_avec_cache', newCache).catch(() => {});
      console.debug('[AvecSync] sync OK', key, '→ R$', data?.network?.faturamento || 0);
      // No force mode, também limpa snapshot pra reprocessar
      if (opts.force) {
        const allSnaps = getSnapshots();
        if (allSnaps[key]) {
          delete allSnaps[key];
          localStorage.setItem('fk_avec_snapshots', JSON.stringify(allSnaps));
          cloudSave('fk_avec_snapshots', allSnaps).catch(() => {});
          setSnapshotsState(getSnapshots());
        }
      }
      // Auto-consolidar meses fechados
      const consolidated = consolidateClosedMonths(newCache);
      if (consolidated.length > 0) {
        const newSnaps = getSnapshots();
        setSnapshotsState(newSnaps);
        cloudSave('fk_avec_snapshots', newSnaps).catch(() => {});
      }
    } catch (err) {
      setError(err.message);
      console.error('[AvecSync] erro:', err);
    } finally {
      setLoading(false);
      inflightRef.current = false;
    }
  }, [selectedYear, selectedMonth]); // cache removido — usamos forma funcional do setState

  // Mantém doSyncRef apontando pra última versão do doSync, pro setInterval
  // sempre chamar a função atualizada (e não a captura inicial via closure).
  useEffect(() => { doSyncRef.current = doSync; }, [doSync]);

  // ─── SELF-HEAL no boot ─────────────────────────────────────────────────
  // Se detectar que o sync está muito atrasado OU o cache do mês corrente
  // está vazio, dispara sync forçado imediato (sem esperar 60s do polling).
  // Cobre o caso onde o app esteve fechado por dias e o cache ficou stale.
  const bootHealDoneRef = useRef(false);
  useEffect(() => {
    if (!cloudLoaded || bootHealDoneRef.current) return;
    if (!doSyncRef.current) return;
    if (!temSessao) return; // A8a: só marca como feito quando há sessão (senão nunca rodaria depois do login)
    bootHealDoneRef.current = true;

    const curKey = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
    const curCache = cache[curKey];
    const cacheVazio = !curCache?.units || Object.values(curCache.units).every(
      u => !u || u._error || ((u.faturamento || 0) === 0 && (u.visitas || 0) === 0)
    );

    if (AVEC_ULTIMO_SYNC_JSON) {
      // A8b: relógio do APARELHO, leitura sem NaN; sem registro = não atrasado
      // (sem rajada de sync forçado no 1º boot depois do deploy); sem force.
      let ultimo = null;
      try { ultimo = lerUltimoSyncMs(localStorage.getItem(CHAVE_SYNC_APARELHO)); } catch {}
      const atrasado = syncAtrasado(ultimo);
      if (cacheVazio || atrasado) {
        console.info(`[AvecSync] self-heal (cacheVazio=${cacheVazio}, atrasado=${atrasado}) — sync agora`);
        doSyncRef.current();
      }
      return;
    }

    let lastSyncAge = Infinity;
    try {
      const last = localStorage.getItem(SYNC_META_KEY);
      if (last) lastSyncAge = (Date.now() - new Date(last).getTime()) / 60000;
    } catch {}

    const stale = lastSyncAge > 30; // > 30 min sem sync
    if (cacheVazio || stale) {
      console.info(`[AvecSync] self-heal disparado (cacheVazio=${cacheVazio}, lastSyncAge=${lastSyncAge.toFixed(0)}min) — forçando sync agora`);
      doSyncRef.current(undefined, undefined, { force: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloudLoaded, cache, selectedYear, selectedMonth, temSessao]);

  // ─── Polling automático (60s, em todos os dispositivos) ─────────────────
  // Revisão 23/10/2026 (achado 4): mês PASSADO que já tem dado no cache mas não
  // virou snapshot (relatório faltando) não entra no polling de 60 s: antes da
  // rodada ele virava snapshot no 1º sync e o polling parava sozinho. Ele ganha
  // o sync imediato ao ser escolhido e as novas tentativas com teto do
  // syncLast12IfNeeded. O mês corrente segue a cada 60 s, sem mudança.
  const pularPollingMesPassadoRef = useRef(false);
  pularPollingMesPassadoRef.current = AVEC_COLETA_ESTADO && isPastMonth && !isConsolidated && hasAnyRealData(cache[monthKey]);
  useEffect(() => {
    if (!autoSync || isConsolidated || !temSessao) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    const ms = Math.max(MIN_INTERVAL_MS, intervalMs);
    const tick = () => {
      // Só sincroniza se a aba está visível — economiza chamadas em background.
      // Quando a aba volta a ficar visível, o handler de visibilitychange
      // dispara um sync imediato.
      if (pularPollingMesPassadoRef.current) return;
      if (pausadoNoCrm()) return;
      if (document.visibilityState === 'visible' && doSyncRef.current) {
        console.debug('[AvecSync] polling tick →', new Date().toLocaleTimeString());
        doSyncRef.current();
      }
    };
    // Sync imediato ao montar / mudar mês
    if (doSyncRef.current && !pausadoNoCrm()) doSyncRef.current();
    // 28/10/2026, pedido do dono: "quando a gente quiser ver uma atualização do Avec, a gente mesmo
    // recarrega a página". Sem AVEC_SYNC_PERIODICO: só o sync ao abrir/mudar o mês e o botão manual.
    if (AVEC_SYNC_PERIODICO) intervalRef.current = setInterval(tick, ms);

    // Quando o usuário volta à aba, dispara sync imediato (sem esperar o próximo tick)
    const onVis = () => {
      if (pularPollingMesPassadoRef.current) return;
      if (pausadoNoCrm()) return;
      if (document.visibilityState === 'visible' && doSyncRef.current) {
        doSyncRef.current();
      }
    };
    if (AVEC_SYNC_PERIODICO) document.addEventListener('visibilitychange', onVis);
    // saiu do CRM: o sync imediato que ficou pausado
    const pararOuvirCrm = CRM_SEM_RECARGA && AVEC_SYNC_PERIODICO ? ouvirRotaCrm((ativa) => { if (!ativa) onVis(); }) : null;

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener('visibilitychange', onVis);
      pararOuvirCrm?.();
    };
  }, [autoSync, isConsolidated, selectedYear, selectedMonth, intervalMs, temSessao]);

  // ─── Sync diário (Meta x Realizado) ───────────────────────────────────
  const dailySyncInflightRef = useRef(false);
  // Anti-trava: igual ao doSync, libera após 90s
  const dailySyncStartRef = useRef(0);
  // A9: a 1ª passada da grade nesta sessão já terminou? (o Dashboard da gerente
  // só acusa dia ausente depois disso)
  const [gradeDiariaPronta, setGradeDiariaPronta] = useState(false);
  const doDailySync = useCallback(async (year, month, opts = {}) => {
    if (!isAvecConfigured()) return;
    if (!temSessaoRef.current) return; // A8a
    if (dailySyncInflightRef.current) {
      const stuck = Date.now() - dailySyncStartRef.current > 90_000;
      if (!stuck) return;
      console.warn('[AvecSync daily] inflight travado >90s — liberando');
      dailySyncInflightRef.current = false;
    }
    dailySyncInflightRef.current = true;
    dailySyncStartRef.current = Date.now();
    try {
      const updated = await syncDailyFaturamentoMonth(year, month, { force: opts.force });
      setDailyCacheState({ ...updated });
      setGradeDiariaPronta(true);
      cloudSave('fk_avec_daily_cache', updated).catch(() => {});
      console.debug('[AvecSync daily] sync OK', `${year}-${String(month).padStart(2,'0')}`);
    } catch (err) {
      console.warn('[AvecSync daily] erro:', err.message);
    } finally {
      dailySyncInflightRef.current = false;
    }
  }, []);

  // Dispara sync diário do faturamento (Meta x Realizado).
  //
  // Regras:
  //   1. MÊS CORRENTE — sync ao montar + polling de 60s. Refetch contínuo
  //      pra capturar comandas novas e ajustes retroativos.
  //   2. MÊS RECÉM-FECHADO (imediatamente anterior) — UM refetch FORÇADO
  //      por sessão. Garante que o último dia do mês fique completo: até
  //      ontem o dia 30 podia ter valor parcial (sync das 23:50 não pegou
  //      comandas que entraram depois). Sem isso, ao virar snapshot o valor
  //      ficaria congelado parcial pra sempre. Uma vez forçado, fica imutável.
  //   3. MESES MAIS ANTIGOS — sem sync (já estão estáveis há tempos).
  useEffect(() => {
    if (!autoSync || !temSessao) return;

    const now = new Date();
    const curY = now.getFullYear();
    const curM = now.getMonth() + 1;
    const isCurrentMonth = selectedYear === curY && selectedMonth === curM;

    // Calcula o mês imediatamente anterior (cobre virada de ano)
    const prev = new Date(curY, curM - 2, 1);
    const isImmediatePrevMonth =
      selectedYear === prev.getFullYear() && selectedMonth === (prev.getMonth() + 1);

    // Mês passado mais antigo: nada a fazer
    if (!isCurrentMonth && !isImmediatePrevMonth) return;

    // Mês recém-fechado: força refetch UMA vez por sessão pra finalizar valores
    if (isImmediatePrevMonth) {
      const key = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`;
      if (!dailyFinalizeDoneRef.current.has(key)) {
        dailyFinalizeDoneRef.current.add(key);
        doDailySync(selectedYear, selectedMonth, { force: true });
      }
      return; // não faz polling de mês passado
    }

    // Mês corrente: sync inicial + polling 60s (só com aba visível; pausado com o CRM aberto)
    if (!pausadoNoCrm()) doDailySync(selectedYear, selectedMonth);
    if (!AVEC_SYNC_PERIODICO) return undefined;
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible' && !pausadoNoCrm()) {
        doDailySync(selectedYear, selectedMonth);
      }
    }, 60_000);
    const pararOuvirCrm = CRM_SEM_RECARGA
      ? ouvirRotaCrm((ativa) => { if (!ativa && document.visibilityState === 'visible') doDailySync(selectedYear, selectedMonth); })
      : null;
    return () => { clearInterval(interval); pararOuvirCrm?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth, autoSync, temSessao]);

  // ─── Sincronização inicial: último 12 meses (apenas o que não tem cache) ─
  // Schema atual — bump quando adicionar campos novos no syncUnitRange.
  // Snapshots/caches com schema antigo são re-sincados forçadamente.
  const CURRENT_SCHEMA_VERSION = 2;
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const syncLast12IfNeeded = useCallback(async () => {
    if (!isAvecConfigured() || initialLoadDone) return;
    if (!temSessao) return; // A8a: espera o login (depois roda uma vez)
    setInitialLoadDone(true);
    const now = new Date();
    // Helper: detecta se um snapshot/cache de unidade tem o schema atual
    const hasCurrentSchema = (unitData) => {
      if (!unitData || typeof unitData !== 'object') return false;
      return (unitData._schemaVersion || 0) >= CURRENT_SCHEMA_VERSION;
    };
    const monthHasCurrentSchema = (monthData, isSnapshot) => {
      const units = isSnapshot ? monthData : (monthData?.units || {});
      const list = Object.values(units);
      if (list.length === 0) return false;
      // Pelo menos uma unidade precisa ter schema atual (todas devem ter, mas tolera 1 erro)
      return list.some(hasCurrentSchema);
    };
    // Revisão 23/10/2026 (achados 4, 11 e 19): teto das novas tentativas do mês
    // fechado com falha, por aparelho: 1 vez a cada 12 h, até 3 vezes; depois
    // vira snapshot com o que veio (src/utils/avecRessync.js).
    let marcasRessync = {};
    if (AVEC_COLETA_ESTADO) { try { marcasRessync = lerMarcasRessync(localStorage.getItem(CHAVE_RESSYNC)); } catch { marcasRessync = {}; } }
    const salvarMarcas = () => { try { localStorage.setItem(CHAVE_RESSYNC, JSON.stringify(marcasRessync)); } catch { /* só aparelho */ } };
    const desistidos = new Set();
    const mesesDaJanela = new Set();
    for (let i = 1; i <= 11; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const key = `${y}-${String(m).padStart(2, '0')}`;
      mesesDaJanela.add(key);
      const snap = snapshots[key];
      const cached = cache[key];
      const hasSnap = snap && !isSnapshotEmpty(snap);
      const hasCache = cached?.units && Object.values(cached.units).some(u => (u.faturamento || 0) > 0);
      // Snapshots/caches com schema desatualizado precisam ser re-sincados.
      const snapOk = hasSnap && monthHasCurrentSchema(snap, true);
      // 23/10/2026: mês do cache com relatório que falhou precisa re-sincronizar
      // (prioridade mais baixa); snapshot no cofre vence. Falha só de alguns
      // pedidos do leque por pessoa (0024/0065) não conta.
      const soFalha = AVEC_COLETA_ESTADO && hasCache && monthHasCurrentSchema(cached, false) && mesTemFalhas(cached, false, { ignorarLeque: true });
      const cacheOk = hasCache && monthHasCurrentSchema(cached, false) && !soFalha;
      if (snapOk || cacheOk) continue;
      if (soFalha && !hasSnap) {
        const decisao = decidirRessync(marcasRessync[key], Date.now());
        if (decisao === 'esperar') continue;
        if (decisao === 'consolidar') { desistidos.add(key); continue; }
        marcasRessync = registrarTentativa(marcasRessync, key, Date.now());
        salvarMarcas();
      }
      // GUARDA o snapshot antigo (mesmo com schema velho) antes de apagar.
      // Se o re-sync vier com dados piores, restauramos.
      const snapBackup = snap ? JSON.parse(JSON.stringify(snap)) : null;
      if (snap) {
        const allSnaps = getSnapshots();
        delete allSnaps[key];
        localStorage.setItem('fk_avec_snapshots', JSON.stringify(allSnaps));
      }
      try {
        const data = await syncAllUnitsMonth(y, m, { prioridade: PRIORIDADE.HISTORICO });
        // Descarta silenciosamente respostas totalmente vazias (token sandbox / sem acesso)
        if (!hasAnyRealData(data)) {
          // Restaura o snapshot original se o sync deu vazio
          if (snapBackup) {
            const allSnaps = getSnapshots();
            allSnaps[key] = snapBackup;
            localStorage.setItem('fk_avec_snapshots', JSON.stringify(allSnaps));
          }
          continue;
        }
        // Compara faturamento total: se o novo for MUITO menor que o snapshot anterior, restaura
        if (snapBackup) {
          const prevFat = Object.values(snapBackup).reduce((s, u) => s + (Number(u?.faturamento) || 0), 0);
          const nextFat = Object.values(data.units || {}).reduce((s, u) => s + (Number(u?.faturamento) || 0), 0);
          if (prevFat > 5000 && nextFat < prevFat * 0.7) {
            // Suspeita de chamada parcial — restaura snapshot
            const allSnaps = getSnapshots();
            allSnaps[key] = snapBackup;
            localStorage.setItem('fk_avec_snapshots', JSON.stringify(allSnaps));
            continue;
          }
        }
        // Merge protegido: mantém o MAIOR de cada campo entre cache atual e novo
        const prev = getCache()[key];
        const mergedData = prepararMesParaGravar(prev, data, { coletaEstado: AVEC_COLETA_ESTADO });
        const newCache = { ...getCache(), [key]: mergedData };
        setCache(newCache);
        setCacheState(newCache);
      } catch (err) {
        console.warn(`[AvecSync] falha ao carregar ${key}:`, err.message);
        // Restaura snapshot em caso de erro
        if (snapBackup) {
          const allSnaps = getSnapshots();
          allSnaps[key] = snapBackup;
          localStorage.setItem('fk_avec_snapshots', JSON.stringify(allSnaps));
        }
      }
    }
    if (AVEC_COLETA_ESTADO) {
      // meses que viraram snapshot ou saíram da janela não precisam mais da marca
      const snapsAgora = getSnapshots();
      marcasRessync = limparMarcas(marcasRessync, (k) => mesesDaJanela.has(k) && !snapsAgora[k]);
      salvarMarcas();
      consolidateClosedMonths(getCache(), { mesmoComFalha: desistidos });
    } else {
      consolidateClosedMonths(getCache());
    }
    setSnapshotsState(getSnapshots());
  }, [cache, snapshots, initialLoadDone, temSessao]);

  useEffect(() => {
    syncLast12IfNeeded();
  }, [syncLast12IfNeeded]);

  // ─── API exposta ────────────────────────────────────────────────────────
  const currentData = isConsolidated
    ? { units: snapshots[monthKey], network: null, _consolidated: true }
    : cache[monthKey] || null;

  // Pega dados de um mês específico (cache ou snapshot)
  function getMonthData(year, month) {
    const key = `${year}-${String(month).padStart(2, '0')}`;
    if (snapshots[key]) return { units: snapshots[key], network: null, _consolidated: true };
    return cache[key] || null;
  }

  // Pega faturamento diário de um mês (rede ou filtrado por unitKey)
  function getDailyFaturamento(year, month, unitKey = null) {
    return getDailyFaturamentoForMonth(year, month, unitKey);
  }

  // ─── Range arbitrário (calendário diário) ─────────────────────────────────
  // Cache em memória: { 'YYYY-MM-DD_YYYY-MM-DD': { data, _fetchedAt } }
  const [rangeCache, setRangeCache] = useState({});
  const rangeInflightRef = useRef({}); // evita chamadas concorrentes do mesmo range
  // 23/10/2026: pedido enxuto (DASH_PEDIDO_ENXUTO) e cache protegido
  // (RANGE_CACHE_PROTEGIDO) moram em src/utils/rangeStore.js. Com as duas
  // chaves desligadas o fetchRange é o de antes (logo abaixo), sem mudança.
  const rangeStoreRef = useRef(null);
  if ((DASH_PEDIDO_ENXUTO || RANGE_CACHE_PROTEGIDO) && !rangeStoreRef.current) {
    rangeStoreRef.current = criarRangeStore({
      buscar: syncAllUnitsRange,
      retentar: retentarFalhasRange,
      transferirBrutos,
      recalcularRede,
      enxuto: DASH_PEDIDO_ENXUTO,
      protegido: RANGE_CACHE_PROTEGIDO,
      aoMudar: (estado) => setRangeCache(estado),
    });
  }
  const fetchRangeNovo = useCallback((startDate, endDate, opts = {}) => rangeStoreRef.current.fetchRange(startDate, endDate, opts), []);
  // "Tentar de novo" (telas novas): refaz só o que faltou e guarda no lugar.
  const retentarFalhas = useCallback(async (rangeData) => {
    if (rangeStoreRef.current) return rangeStoreRef.current.retentarFalhas(rangeData);
    return retentarFalhasRange(rangeData, { prioridade: 0, prazoMs: 60_000 });
  }, []);
  const fetchRangeAntigo = useCallback(async (startDate, endDate, opts = {}) => {
    const key = `${startDate}_${endDate}`;
    const { force = false, maxAgeMs = 60_000 } = opts;
    const existing = rangeCache[key];
    if (!force && existing) {
      const age = Date.now() - new Date(existing._fetchedAt).getTime();
      if (age < maxAgeMs) return existing;
    }
    if (rangeInflightRef.current[key]) return rangeInflightRef.current[key];
    const promise = (async () => {
      const data = await syncAllUnitsRange(startDate, endDate, {
        skipTaxaRetorno: true,
        skipExtrasPorProf: true,
        withExtras0031: !!opts.withExtras0031, // duelos: extras por prof via 0031 paginado
      });
      setRangeCache(prev => ({ ...prev, [key]: data }));
      return data;
    })();
    rangeInflightRef.current[key] = promise;
    try { return await promise; }
    finally { delete rangeInflightRef.current[key]; }
  }, [rangeCache]);
  const fetchRange = (DASH_PEDIDO_ENXUTO || RANGE_CACHE_PROTEGIDO) ? fetchRangeNovo : fetchRangeAntigo;

  // Cache + dedupe pra extras de UM prof num range específico.
  // Key: 'unitKey|avecProfId|start_end'. Cada chamada = 1 request Avec (0024).
  const proExtrasInflight = useRef({});
  // Bloqueios de agenda de um prof no range (0159) — com dedupe igual extras
  const proBloqueiosInflight = useRef({});
  const fetchProBloqueios = useCallback(async (unitKey, avecProfId, startDate, endDate) => {
    const k = `${unitKey}|${avecProfId}|${startDate}_${endDate}`;
    if (proBloqueiosInflight.current[k]) return proBloqueiosInflight.current[k];
    const promise = (async () => {
      try {
        return await syncProBloqueiosRange(unitKey, avecProfId, startDate, endDate);
      } finally {
        delete proBloqueiosInflight.current[k];
      }
    })();
    proBloqueiosInflight.current[k] = promise;
    return promise;
  }, []);

  const fetchProExtras = useCallback(async (unitKey, avecProfId, startDate, endDate) => {
    const k = `${unitKey}|${avecProfId}|${startDate}_${endDate}`;
    if (proExtrasInflight.current[k]) return proExtrasInflight.current[k];
    const promise = (async () => {
      try {
        return await syncProExtrasRange(unitKey, avecProfId, startDate, endDate);
      } finally {
        delete proExtrasInflight.current[k];
      }
    })();
    proExtrasInflight.current[k] = promise;
    return promise;
  }, []);

  return (
    <AvecSyncContext.Provider value={{
      // Dados
      data: currentData,
      cache,
      snapshots,
      dailyCache,
      getMonthData,
      getDailyFaturamento,
      fetchRange,
      retentarFalhas,
      fetchProExtras,
      fetchProBloqueios,
      rangeCache,
      // Estado
      loading,
      gradeDiariaPronta,
      lastSync,
      error,
      autoSync,
      isConsolidated,
      isPastMonth,
      selectedYear,
      selectedMonth,
      monthKey,
      // Ações
      refresh: () => doSync(),
      refreshMonth: (y, m) => doSync(y, m),
      // refreshAll: dispara sync mensal + diário FORÇADO (usado pelo botão Atualizar)
      // Force mode: bypassa MAX-wins, limpa snapshot, re-fetcha todos os dias
      refreshAll: async () => {
        await doSync(undefined, undefined, { force: true });
        await doDailySync(selectedYear, selectedMonth, { force: true });
      },
      setMonth: (y, m) => { setSelectedYear(y); setSelectedMonth(m); },
      setAutoSync,
      // Configuração
      configured: isAvecConfigured(),
    }}>
      {children}
    </AvecSyncContext.Provider>
  );
}

export function useAvecSync() {
  const ctx = useContext(AvecSyncContext);
  if (!ctx) throw new Error('useAvecSync deve ser usado dentro de <AvecSyncProvider>');
  return ctx;
}
